import { type FC } from "react"

import type { CourseEditProposedChange } from "@forento/core/models/aiCourse"

import AiProposedChanges, { BeforeAfter, ContentBlock, ExpandableRow, PlainRow } from "~/components/AiProposedChanges"

type Props = {
	changes: CourseEditProposedChange[]
	decision: "applied" | "discarded" | null
	isApplying: boolean
	isDiscarding: boolean
	onApply(): void
	onDiscard(): void
}

const ProposedChanges: FC<Props> = ({ changes, decision, isApplying, isDiscarding, onApply, onDiscard }) => (
	<AiProposedChanges
		changeCount={changes.length}
		decision={decision}
		isApplying={isApplying}
		isDiscarding={isDiscarding}
		onApply={onApply}
		onDiscard={onDiscard}
	>
		{changes.map((change, index) => (
			<ChangeRow key={index} change={change} />
		))}
	</AiProposedChanges>
)

const ChangeRow: FC<{ change: CourseEditProposedChange }> = ({ change }) => {
	switch (change.type) {
		case "course-title":
			return <PlainRow label="Course title" detail={`"${change.before}" → "${change.after}"`} />
		case "course-description":
			return (
				<ExpandableRow label="Course description updated">
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "chapter-rename":
			return <PlainRow label={`Renamed chapter "${change.before}" to "${change.after}"`} />
		case "page-rename":
			return (
				<PlainRow label={`Renamed page "${change.before}" to "${change.after}"`} detail={change.chapterTitle} />
			)
		case "page-rewrite":
			return (
				<ExpandableRow
					label={`Rewrote page "${change.pageTitle}"`}
					detail={[
						change.chapterTitle,
						...(change.newTitle !== null ? [`retitled to "${change.newTitle}"`] : []),
						...(change.quizAnswersChanged ? ["quiz answers updated"] : []),
					].join(" · ")}
				>
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "page-create":
			return (
				<ExpandableRow label={`New ${change.pageType} page "${change.pageTitle}"`} detail={change.chapterTitle}>
					<ContentBlock tone="added" title="Content">
						{change.content}
					</ContentBlock>
				</ExpandableRow>
			)
		case "chapter-create":
			return (
				<ExpandableRow
					label={`New chapter "${change.chapterTitle}"`}
					detail={`${change.pages.length} ${change.pages.length === 1 ? "page" : "pages"}`}
				>
					<div className="flex flex-col gap-2">
						{change.pages.map((page, pageIndex) => (
							<ContentBlock key={pageIndex} tone="added" title={`${page.pageTitle} (${page.pageType})`}>
								{page.content}
							</ContentBlock>
						))}
					</div>
				</ExpandableRow>
			)
	}
}

export default ProposedChanges
